import { Link } from "react-router-dom";
import DoospLayout from "@/components/doosp/DoospLayout";
import { PROGRAMS, DOOSP_INFO } from "@/lib/programs";
import LegalDisclaimer from "@/components/LegalDisclaimer";
import HomeFAQ from "@/components/HomeFAQ";
import {
  ArrowRight, CheckCircle2, ShieldCheck, Globe2, GraduationCap, Building2, TrendingUp,
  ShieldAlert, HeartPulse, Flame, Biohazard, ArrowUpRight, Sparkles, Users,
} from "lucide-react";

// Map program icon strings → Lucide components
const ICONS = { ShieldAlert, ShieldCheck, HeartPulse, Flame, Biohazard };

function ProgramCard({ program }) {
  const Icon = ICONS[program.icon] || ShieldCheck;
  const isLive = program.status === "live";
  return (
    <Link
      to={`/programs/${program.slug}`}
      className={`group relative overflow-hidden rounded-xl border-2 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
        isLive ? "border-red-600" : "border-zinc-200 hover:border-zinc-400"
      }`}
      data-testid={`home-program-${program.slug}`}
    >
      {isLive && (
        <div className="absolute top-4 right-4 text-[10px] font-mono uppercase tracking-widest bg-red-600 text-white px-2 py-1 rounded">
          Live
        </div>
      )}
      {!isLive && (
        <div className="absolute top-4 right-4 text-[10px] font-mono uppercase tracking-widest bg-zinc-100 text-zinc-500 px-2 py-1 rounded">
          Coming soon
        </div>
      )}
      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg mb-4 bg-${program.color}-50`}>
        <Icon className={`h-6 w-6 text-${program.color}-600`} />
      </div>
      <div className="font-display font-bold text-xl leading-tight">{program.name}</div>
      <div className="text-xs text-zinc-500 font-mono mt-1">{program.tagline}</div>
      <p className="text-sm text-zinc-600 mt-3 line-clamp-3 leading-relaxed">{program.summary}</p>
      <div className="mt-5 flex items-center justify-between">
        <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">
          {program.modules} modules · {program.durationLabel}
        </span>
        <ArrowUpRight className={`h-4 w-4 text-zinc-400 group-hover:text-red-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition`} />
      </div>
    </Link>
  );
}

export default function Home() {
  const flagship = PROGRAMS.find((p) => p.status === "live");

  return (
    <DoospLayout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-zinc-50 border-b border-zinc-200">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, #7F1D1D 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 border border-red-800 bg-white/80 backdrop-blur px-3 py-1.5 rounded-full mb-6 shadow-sm">
                <ShieldCheck className="h-3.5 w-3.5 text-red-800" />
                <span className="text-xs font-mono uppercase tracking-widest text-red-800">
                  {DOOSP_INFO.parent} · {DOOSP_INFO.short}
                </span>
              </div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02]" data-testid="home-hero-heading">
                Global standards.<br />
                <span className="text-red-800">Safer workplaces.</span>
              </h1>
              <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-2xl">
                The <strong className="text-zinc-900">Department of Occupational Safety Programs</strong> is a specialty
                division of {DOOSP_INFO.parentFull} (IMCAC) — an independent competency
                assessment and certification body offering competency-based credentials across
                bloodborne pathogens, infection control, healthcare worker safety, laser safety, and clinical waste management.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/programs" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white h-12 px-7 rounded-md text-base font-medium shadow-lg shadow-red-600/20 transition" data-testid="home-cta-programs">
                  Explore Programs <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/corporate" className="inline-flex items-center gap-2 h-12 px-6 rounded-md border-2 border-zinc-900 hover:bg-zinc-900 hover:text-white text-sm font-medium transition" data-testid="home-cta-corporate">
                  Certify Your Team <Building2 className="h-4 w-4" />
                </Link>
                <Link to="/verify" className="text-sm font-medium text-zinc-600 hover:text-red-800 underline decoration-red-600 decoration-2 underline-offset-4">
                  Verify a certificate →
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg border-t border-zinc-200 pt-6">
                <div>
                  <div className="font-display text-2xl font-black text-red-800">5</div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Programs</div>
                </div>
                <div>
                  <div className="font-display text-2xl font-black text-red-800">OSHA</div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Aligned</div>
                </div>
                <div>
                  <div className="font-display text-2xl font-black text-red-800">2 yrs</div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Validity</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden border-2 border-zinc-200 shadow-2xl bg-white">
                <img
                  src="/sample_certificate.jpg"
                  alt="Sample DOOSP certificate"
                  className="w-full h-auto"
                />
                <div className="absolute -bottom-3 -right-3 bg-red-600 text-white text-[10px] font-mono uppercase tracking-widest px-3 py-2 rounded shadow-lg">
                  Print-ready · QR verified
                </div>
              </div>
              <div className="absolute -top-4 -left-4 rotate-[-4deg] bg-emerald-100 border-2 border-emerald-600 rounded-lg px-4 py-2 shadow-lg">
                <div className="text-[10px] font-mono uppercase text-emerald-800">Trusted by</div>
                <div className="font-display font-bold text-sm text-emerald-900">Global healthcare teams</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION STATEMENT */}
      <section className="border-b border-zinc-200" data-testid="home-mission">
        <div className="max-w-5xl mx-auto px-6 py-20 text-center">
          <div className="text-xs font-mono uppercase tracking-widest text-red-800">Our mission</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3 leading-tight">
            {DOOSP_INFO.tagline}.
          </h2>
          <p className="text-zinc-600 mt-6 max-w-3xl mx-auto leading-relaxed">
            We equip healthcare workers, safety officers, and organizations with the evidence-based
            training they need to protect themselves, their patients, and their communities — with
            a rigorous competency-based approach whose curricula are aligned to OSHA, CDC, and WHO guidance.
          </p>

          <div className="mt-10">
            <LegalDisclaimer />
          </div>
        </div>
      </section>

      {/* PROGRAMS GRID */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="text-xs font-mono uppercase tracking-widest text-red-800">Certification programs</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 leading-tight">
                Five pillars of occupational safety.
              </h2>
              <p className="text-zinc-600 mt-3">
                From our flagship Bloodborne Pathogens certification to specialty programs launching soon.
              </p>
            </div>
            <Link to="/programs" className="text-sm font-medium text-red-700 hover:text-red-900 inline-flex items-center gap-1.5">
              View all programs <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROGRAMS.map((p) => <ProgramCard key={p.slug} program={p} />)}
          </div>
        </div>
      </section>

      {/* FLAGSHIP SPOTLIGHT — BBP */}
      {flagship && (
        <section className="border-b border-zinc-200 bg-zinc-900 text-white" data-testid="home-flagship">
          <div className="max-w-7xl mx-auto px-6 py-24">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 bg-red-950 border border-red-500 rounded-full px-3 py-1.5 mb-6">
                  <Sparkles className="h-3.5 w-3.5 text-red-400" />
                  <span className="text-xs font-mono uppercase tracking-widest text-red-300">Flagship program · Available now</span>
                </div>
                <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-tight">
                  Bloodborne Pathogens Training <span className="text-red-400">&amp; Certification.</span>
                </h2>
                <p className="text-zinc-300 mt-5 leading-relaxed max-w-xl">
                  OSHA 29 CFR 1910.1030-aligned course with 13 interactive modules, a 50-MCQ exam,
                  and an AI-graded case study tailored to your profession. Complete in 45–60
                  minutes and receive a verifiable 2-year certificate.
                </p>
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { label: "Modules", value: "13" },
                    { label: "Exam", value: "50 MCQ" },
                    { label: "AI grading", value: "Yes" },
                    { label: "Duration", value: "45–60 min" },
                  ].map((s) => (
                    <div key={s.label} className="border-l-2 border-red-500 pl-3">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{s.label}</div>
                      <div className="font-display text-xl font-bold mt-1">{s.value}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link to={`/programs/${flagship.slug}`} className="bg-red-600 hover:bg-red-700 text-white h-11 px-6 inline-flex items-center gap-2 rounded-md text-sm font-medium">
                    Program details <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/register" className="border-2 border-white/20 hover:bg-white/10 text-white h-11 px-6 inline-flex items-center rounded-md text-sm font-medium">
                    Start certification
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="rounded-xl overflow-hidden border border-zinc-800 shadow-2xl">
                  <img src={flagship.heroImage} alt={flagship.name} className="w-full h-72 md:h-96 object-cover" />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* WHY DOOSP */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-xs font-mono uppercase tracking-widest text-red-800">Why DOOSP</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 leading-tight">
              Rigorous. Independent. Practical.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Globe2, title: "Curricula aligned to global guidance", body: "Content aligned to OSHA, CDC, and WHO guidance — competency-based credentials, not regulatory endorsements." },
              { icon: GraduationCap, title: "Practitioner-designed", body: "Every program is authored and reviewed by working clinicians, biosafety officers, and safety compliance experts." },
              { icon: TrendingUp, title: "Continuously updated", body: "Guidelines evolve — so does our content. New research triggers course updates within the certification cycle." },
            ].map((f) => (
              <div key={f.title} className="text-left">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-red-50 mb-4">
                  <f.icon className="h-6 w-6 text-red-800" />
                </div>
                <div className="font-display font-bold text-xl">{f.title}</div>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-gradient-to-r from-red-800 to-red-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-red-200">Ready to begin?</div>
            <h3 className="font-display text-2xl md:text-3xl font-bold mt-2">
              Certify one professional or an entire team.
            </h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/register" className="bg-white text-red-800 hover:bg-red-50 h-11 px-6 inline-flex items-center gap-2 rounded-md text-sm font-medium">
              <Users className="h-4 w-4" /> I&rsquo;m one professional
            </Link>
            <Link to="/corporate" className="border-2 border-white/40 hover:bg-white/10 text-white h-11 px-6 inline-flex items-center gap-2 rounded-md text-sm font-medium">
              <Building2 className="h-4 w-4" /> Certify a team
            </Link>
          </div>
        </div>
      </section>

      <HomeFAQ />
    </DoospLayout>
  );
}
